import {Box, Grid, makeStyles} from '@material-ui/core'
import React from 'react'
import {BikeIcon, CloudIcon, DeliveryBoxIcon, GeoZoneIcon, HourGlassIcon, Pay, Pickup, ShipmentDelivered, TruckIcon} from '../../Icons'
import {VisitorRole, VisitorRoleValues} from 'web-admin/src/api/organization'
import {DashboardGridItem} from "./DashboardGridItem";
import {displayNumber} from "web-admin/src/utils";
import {PoiDashboardStats} from "web-admin/src/api/dashboard";

const useStyles = makeStyles(() => ({
  gridRoot: {
    maxWidth: '800px',
    minWidth: '600px',
    marginBottom: '20px'
  }
}))

interface DashboardGridProps {
  dashboardStats: PoiDashboardStats
  isAdmin: boolean
  visitorRoles?: Nullable<string[]>
}

export const DashboardGrid = (props: DashboardGridProps) => {
  const {
    dashboardStats,
    isAdmin,
    visitorRoles = null
  } = props
  const classes = useStyles()

  const isNotVisitorOrHasRole = (role: VisitorRole) =>
    !visitorRoles || visitorRoles?.includes(role.role)

  const isAdminOrVisitorWithRole = (role: VisitorRole) =>
    isAdmin || (!!visitorRoles && visitorRoles?.includes(role.role))

  return (
    <div>
      <Box display='flex' justifyContent='space-around' flexWrap='wrap'>
        <Grid container spacing={3} className={classes.gridRoot}>
          <DashboardGridItem
            icon={Pay}
            indicator={`${displayNumber(dashboardStats.bonusTotal, 2)} €`}
            description={`Total de la prime`}
            tooltip={`
              ${displayNumber(dashboardStats.bonusToDeduce, 2)} € à déduire
              ${displayNumber(dashboardStats.bonusToReport, 2)} € à reporter
              ${displayNumber(dashboardStats.bonusDeduced, 2)} € déduit
              ${displayNumber(dashboardStats.bonusPaid, 2)} € payé
              ${displayNumber(dashboardStats.bonusCreated, 2)} € en construction
              ${displayNumber(dashboardStats.bonusInvalid, 2)} € en incident
            `.trim()}
            display={isNotVisitorOrHasRole(VisitorRoleValues.READ_DASHBOARD_BONUS_TOTAL)}
          />
          <DashboardGridItem
            icon={Pay}
            indicator={`${displayNumber(dashboardStats.bonusPerPackage, 2)} €`}
            description={`Prime par colis`}
            display={isNotVisitorOrHasRole(VisitorRoleValues.READ_DASHBOARD_BONUS_PER_PACKAGE)}
          />
          <DashboardGridItem
            icon={HourGlassIcon}
            indicator={dashboardStats.durationTotal}
            description={`Temps total de livraison`}
            tooltip={isAdmin ? `dont ${dashboardStats.autoDeclaredDuration} transmis via API` : undefined}
            display={isNotVisitorOrHasRole(VisitorRoleValues.READ_DASHBOARD_DELIVERY_TIME)}
          />
          <DashboardGridItem
            icon={ShipmentDelivered}
            indicator={displayNumber(dashboardStats.activeDeliveryPointCount)}
            description={`Nombre de livraisons actives`}
            display={isNotVisitorOrHasRole(VisitorRoleValues.READ_DASHBOARD_ACTIVE_DELIVERIES)}
          />
          <DashboardGridItem
            icon={DeliveryBoxIcon}
            indicator={displayNumber(dashboardStats.activePackCount)}
            description={`Colis et palettes`}
            tooltip={isAdmin ? `dont ${displayNumber(dashboardStats.autoActivePackCount)} colis transmis via API` : undefined}
            display={isNotVisitorOrHasRole(VisitorRoleValues.READ_DASHBOARD_ACTIVE_PACK_COUNT)}
          />
          <DashboardGridItem
            icon={DeliveryBoxIcon}
            indicator={displayNumber(dashboardStats.unwrappedPalletsPackCount)}
            description={`Équivalent colis`}
            display={isNotVisitorOrHasRole(VisitorRoleValues.READ_DASHBOARD_ACTIVE_PACK_COUNT)}
          />
          <DashboardGridItem
            icon={Pickup}
            indicator={dashboardStats.hubCount}
            description={`Nombre de HUB`}
            display={isNotVisitorOrHasRole(VisitorRoleValues.READ_DASHBOARD_HUB_COUNT)}
          />
          <DashboardGridItem
            icon={BikeIcon}
            indicator={`${dashboardStats.averageApproachDurationBike} / ${dashboardStats.averageApproachDurationBikeWeighted}`}
            description={`Durée d'approche moyenne (des acteurs / des livraisons)`}
            tooltip={`- "des acteurs" : moyenne des valeurs des différents acteurs\n- "des livraisons" : moyenne des valeurs des acteurs pondérées par leur nombre de livraisons`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_AVERAGE_DISTANCE_PER_DELIVERY)}
          />
          <DashboardGridItem
            icon={BikeIcon}
            indicator={`${displayNumber(dashboardStats.averageApproachDistanceWalkBike)} m / ${displayNumber(dashboardStats.averageApproachDistanceWalkBikeWeighted)} m`}
            tooltip={`- "des acteurs" : moyenne des valeurs des différents acteurs\n- "des livraisons" : moyenne des valeurs des acteurs pondérées par leur nombre de livraisons`}
            description={`Distance d'approche moyenne (des acteurs / des livraisons)`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_AVERAGE_DISTANCE_PER_DELIVERY)}
          />
          <DashboardGridItem
            icon={BikeIcon}
            indicator={`${displayNumber(dashboardStats.averageApproachDistancePerDeliveryWalkBike)} m / ${displayNumber(dashboardStats.averageApproachDistancePerDeliveryWalkBikeWeighted)} m`}
            tooltip={`- "des acteurs" : moyenne des valeurs des différents acteurs\n- "des livraisons" : moyenne des valeurs des acteurs pondérées par leur nombre de livraisons`}
            description={`Distance d'approche moyenne par point (des acteurs / des livraisons)`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_AVERAGE_DISTANCE_PER_DELIVERY)}
          />
          <DashboardGridItem
            icon={BikeIcon}
            indicator={`${displayNumber(dashboardStats.averageDistancePerDeliveryWalkBike)} m / ${displayNumber(dashboardStats.averageDistancePerDeliveryWalkBikeWeighted)} m`}
            tooltip={`- "des acteurs" : moyenne des valeurs des différents acteurs\n- "des livraisons" : moyenne des valeurs des acteurs pondérées par leur nombre de livraisons`}
            description={`Mètres moyen point à point (des acteurs / des livraisons)`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_AVERAGE_DISTANCE_PER_DELIVERY)}
          />
          <DashboardGridItem
            icon={DeliveryBoxIcon}
            indicator={displayNumber(dashboardStats.tourAmount)}
            description={`Nombre de tournées`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_TOUR_AMOUNT)}
          />
          <DashboardGridItem
            icon={GeoZoneIcon}
            indicator={`${displayNumber(dashboardStats.distanceTotalWalkBikeAverage)} km`}
            description={`Distance totale vélo parcourue`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_DISTANCE_TOTAL)}
          />
          <DashboardGridItem
            icon={TruckIcon}
            indicator={`${displayNumber(dashboardStats.thermalKmAvoided)} km / ${displayNumber(dashboardStats.totalThermalKmAvoided)} km`}
            description={`Km vul thermique évité (en tournée / total)`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_THERMAL_KM_AVOIDED)}
          />
          <DashboardGridItem
            icon={CloudIcon}
            indicator={`${displayNumber(dashboardStats.co2TonsAvoided)} tonnes`}
            description={`Tonnes de CO2 évitées`}
            tooltip={`calculs basés sur l'étude du CEREMA du 20 avril 2021 “Émissions routières des polluants atmosphériques Courbes et facteurs d’influence” réalisée en utilisant la méthodologie Copert 5 en considérant les émissions d’un parc roulant composé à 90% de véhicules utilitaires légers (part des livraisons confiées par des expressistes) et à 10% de poids lourd (part des livraisons confiées par des messagers) à la vitesse moyenne de 12km/h représentative de celles constatées en logistique urbaine en zone dense.`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_CO2_AVOIDED)}
          />
          <DashboardGridItem
            icon={CloudIcon}
            indicator={`${displayNumber(dashboardStats.noxTonsAvoided, 2)} tonnes / ${(dashboardStats.ppmTonsAvoided * 1000).toFixed(0)} kg`}
            description={<>NOX / PM<sub>10</sub> évitées</>}
            tooltip={`calculs basés sur l'étude du CEREMA du 20 avril 2021 “Émissions routières des polluants atmosphériques Courbes et facteurs d’influence” réalisée en utilisant la méthodologie Copert 5 en considérant les émissions d’un parc roulant composé à 90% de véhicules utilitaires légers (part des livraisons confiées par des expressistes) et à 10% de poids lourd (part des livraisons confiées par des messagers) à la vitesse moyenne de 12km/h représentative de celles constatées en logistique urbaine en zone dense.`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_NOX_PPM_AVOIDED)}
          />
          <DashboardGridItem
            icon={CloudIcon}
            indicator={`${displayNumber(dashboardStats.fuelAvoided)} L`}
            description={`Volume de carburant évité`}
            display={isAdminOrVisitorWithRole(VisitorRoleValues.READ_DASHBOARD_NOX_PPM_AVOIDED)}
          />
        </Grid>
      </Box>
    </div>
  )
}
